import {BalloonEventModel, DescriptionType, getFrontendUrl} from "ts-core";
import React, {useMemo, useState} from "react";
import {FiLink, FiMail, FiShare2} from "react-icons/fi";
import {FaXTwitter, FaWhatsapp, FaFacebookF} from "react-icons/fa6";
import {useTranslations} from "../../contexts/Translations";
import {trackMixpanel} from "../../utils/mixpanel";
import {Dialog} from "../Dialog";

export interface ShareButtonProps {
    event: BalloonEventModel;
    trackingSource?: string;
}

export function ShareButton({event, trackingSource}: ShareButtonProps) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const frontendBaseUrl = getFrontendUrl();
    const {language, translated} = useTranslations();

    const shareUrl = useMemo(() => {
        const slug = (event as BalloonEventModel)?.slug ?? event?.id;
        if (!slug) return typeof window !== "undefined" ? window.location.href : frontendBaseUrl;
        return `${frontendBaseUrl}/event/${slug}`;
    }, [frontendBaseUrl, event]);

    const title = event?.title ?? "Balloon Events";
    const text =
        (event as BalloonEventModel)?.getDescription(DescriptionType.MainDescription)?.getText(language) ??
        "Finde Veranstaltungen in deiner Nähe";

    const shareData = {title, text, url: shareUrl};

    const canNativeShare = () => {
        if (typeof navigator === "undefined" || !("share" in navigator)) return false;
        return !navigator.canShare || navigator.canShare(shareData);
    };

    const trackComplete = (channel: string) => {
        trackMixpanel('EventDetailPage', 'Share:Complete', {
            eventId: event.id,
            channel,
            source: trackingSource,
        });
    };

    const handleOpen = () => {
        trackMixpanel('EventDetailPage', 'Share:Click', {
            eventId: event.id,
            eventName: event.title,
            source: trackingSource,
        });
        setOpen(true);
    };

    async function handleNativeShare() {
        try {
            await navigator.share(shareData);
            trackComplete('native');
            setOpen(false);
        } catch {
        }
    }

    async function copyLink() {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            trackComplete('copy');
        } catch {
        }
    }

    const optionCls =
        "w-full rounded-xl border border-gray-300 px-4 py-3 flex items-center gap-3 hover:bg-gray-50 " +
        "!text-gray-900 hover:!text-gray-900 visited:!text-gray-900 active:!text-gray-900 focus:!text-gray-900 " +
        "!no-underline";
    return (
        <>
            <button
                onClick={handleOpen}
                className={"flex justify-center items-center w-[36px] h-[36px] text-balloon hover:text-white bg-transparent hover:bg-balloon rounded-full p-2"}
                aria-haspopup="dialog"
                aria-expanded={open || undefined}
                aria-label={translated("share_event")}
                title={translated("share_event")}
            >
                <FiShare2 size={20}/>
            </button>

            <Dialog
                open={open}
                onClose={() => {
                    setOpen(false);
                    setCopied(false);
                }}
                title={translated("share_event")}
                headerContent={
                    <div className="flex items-center gap-3">
                        {event?.images?.[0]?.url ? (
                            <img
                                src={event.images[0].url}
                                alt={event.images[0].text || title}
                                className="w-11 h-11 rounded-lg object-cover"
                            />
                        ) : (
                            <div className="w-11 h-11 rounded-lg bg-gray-200"/>
                        )}
                        <div className="text-sm text-gray-700 min-w-0">
                            <div className="font-medium truncate">{title}</div>
                            <div className="truncate">
                                {(event as BalloonEventModel)?.address?.locality
                                    ? (event as BalloonEventModel)?.address?.locality + " · "
                                    : ""}
                                {(event as BalloonEventModel)?.getDescription(DescriptionType.MainDescription)?.getText(language) ??
                                    "Balloon Events"}
                            </div>
                        </div>
                    </div>
                }
            >
                <div className="grid grid-cols-2 gap-3">
                    <button onClick={copyLink} className={optionCls}>
                        <FiLink className="w-5 h-5 shrink-0"/>
                        <span className="font-medium truncate">
              {copied ? translated("share_link_copied") : translated("share_copy_link")}
            </span>
                    </button>

                    <a
                        className={optionCls}
                        href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(
                            `${text}\n\n${shareUrl}`
                        )}`}
                        onClick={() => {
                            trackComplete('email');
                            setOpen(false);
                        }}
                    >
                        <FiMail className="w-5 h-5 shrink-0"/>
                        <span className="font-medium truncate">E-Mail</span>
                    </a>

                    <a
                        className={optionCls}
                        href={`https://wa.me/?text=${encodeURIComponent(`${title}\n${shareUrl}`)}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                            trackComplete('whatsapp');
                            setOpen(false);
                        }}
                    >
                        <FaWhatsapp className="w-5 h-5 shrink-0"/>
                        <span className="font-medium truncate">WhatsApp</span>
                    </a>

                    <a
                        className={optionCls}
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                            trackComplete('facebook');
                            setOpen(false);
                        }}
                    >
                        <FaFacebookF className="w-5 h-5 shrink-0"/>
                        <span className="font-medium truncate">Facebook</span>
                    </a>

                    <a
                        className={optionCls}
                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                            title
                        )}&url=${encodeURIComponent(shareUrl)}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                            trackComplete('twitter');
                            setOpen(false);
                        }}
                    >
                        <FaXTwitter className="w-5 h-5 shrink-0"/>
                        <span className="font-medium truncate">Twitter</span>
                    </a>

                    {canNativeShare() ? <button
                        onClick={() => (canNativeShare() ? handleNativeShare() : setOpen(false))}
                        className={optionCls}
                    >
                        <span className="inline-block w-5 h-5 rounded-full border border-black/50 shrink-0"/>
                        <span className="font-medium truncate">{translated("share_other_options")}</span>
                    </button> : null}
                </div>
            </Dialog>
        </>
    );
}
